import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import { createColumnHelper } from '@tanstack/react-table';
import { Alert } from '@trussworks/react-uswds';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import _isEqual from 'lodash/isEqual';
import { useRequest } from '@stores';
import { useResetDirtyFields } from '@hooks';

import TanStackTableNew from '@components/table/TanStackTable/TanStackTableNew';
import StepHeader from '@components/step-header/StepHeader';
import TextAreaInput from '@components/textarea/TextArea';
import SelectInput from '@components/select/Select';
import LinkButton from '@components/link/linkButton';
import { TableCell } from '@components/table/tableCellComponents/TableCell';
import { EditCell } from '@components/table/tableCellComponents/EditCell';
import AmountUnitTableCell from '@components/table/tableCellComponents/AmountUnitTableCell';
import CreditUnitTableCell from '@components/table/tableCellComponents/CreditUnitTableCell';
import AmountTypeTableCell from '@components/table/tableCellComponents/AmountTypeTableCell';
import WatersNameTableCell from '@components/table/tableCellComponents/WatersNameTableCell';
import BankILFTableCell from '@components/table/tableCellComponents/BankILFTableCell';
import { ProposedCreditAmountTableCell } from '@components/table/tableCellComponents/ProposedCreditAmountTableCell';
import { AreaAmountTableCell } from '@components/table/tableCellComponents/AreaAmountTableCell';
import { ImpactAreaAmountTableCell } from '@components/table/tableCellComponents/ImpactAreaAmountTableCell';
import { ImpactTableCell } from '@components/table/tableCellComponents/ImpactTableCell';
import { PermanentLossTableCell } from '@components/table/tableCellComponents/PermanentLossTableCell';
import MitigationAmountUnitTableCell from '@components/table/tableCellComponents/MitigationAmountUnitTableCell';
import Section from '@components/section/Section';
import {
  formatState,
  formatProperCase,
  formatBooleanString,
  formatMeasurementUnit,
  formatOptionString,
} from '@forms/input-forms/helpers';

import tooltips from './impactsAndMitigation.tooltips';
import options from './impactsAndMitigation.options';
import textContent from './impactsAndMitigation.text';
import {
  getDefaultValues,
  getGpSchema,
  getIpSchema,
  ilfValidationSchema,
  impactsValidationSchema,
  prmValidationSchema,
} from './impactsAndMitigation.validation';

import { setNumberValue } from '@src/utils/regex';
import { filterNullEmptyObjects } from '@src/utils/helpers';
import TypeOfMaterialsTableCell from '@components/table/tableCellComponents/TypeOfMaterialsTableCell';

import ErrorSummary from '@components/error-summary/ErrorSummary';
import { RibitsQueries } from '@src/api/services/ribits';
import { FileTypes, ResourceFiles, ImpactsResourceType, PermitTypes } from '@src/utils/enums';
import { useGetAquaticResourcesWaterNames } from '@src/api/services/aquatic-resources/hooks';

export const PermitFormImpactsMitigationMetadata = {
  sectionName: 'Impacts and Mitigation',
};

const areaOptions = [
  { value: 'Square Feet', label: 'Square Feet' },
  { value: 'Acres', label: 'Acres' },
];

const volumeOptions = [{ value: 'Cubic Yards', label: 'Cubic Yards' }];

const currentEnv = import.meta.env.VITE_ENVIRONMENT;
const useLegacyEndpoints = currentEnv === 'development' || currentEnv === 'demo' || currentEnv === 'local';
// normalize undefined/empty CSV values
const formatNumericField = (value) => (value != null && value !== '' && Number(value) !== 0 ? String(value) : null);

const SQ_FT_PER_ACRE = 43560;
const toAcres = (impact) => {
  if (!impact) return 0;
  if (impact.amountUnit === 'Acres' && impact.proposedAmount) {
    return Number(impact.proposedAmount);
  }
  if (impact.proposedLength && impact.proposedWidth) {
    return (impact.proposedLength * impact.proposedWidth) / SQ_FT_PER_ACRE;
  }
  return 0;
};

const PermitFormImpactsMitigation = connect(
  'doUpdatePermitRequest',
  'doUpdatePermitRequestSub',
  'doDownloadFile',
  'selectPermitData',
  'selectRequestAPIData',
  'selectRequestFormData',
  'selectRequestFormDataLocation',
  ({
    doUpdatePermitRequest,
    doUpdatePermitRequestSub,
    doDownloadFile,
    permitData,
    requestAPIData,
    requestFormData,
    requestFormDataLocation,
    stepNo,
  }) => {
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();

    const isGeneralPermit = permitData?.requestedPermitType === PermitTypes.GeneralPermit;

    const [impactsKey, setImpactsKey] = useState(0);
    const [prmKey, setPrmKey] = useState(999);
    const [ilfKey, setIlfKey] = useState(99999);
    const [impactsTableErrors, setImpactsTableErrors] = useState();
    const [prmTableErrors, setPrmTableErrors] = useState();
    const [ilfTableErrors, setIlfTableErrors] = useState();
    const [impactsData, setImpactsData] = useState([]);
    const [prmData, setPrmData] = useState([]);
    const [ilfData, setIlfData] = useState([]);
    const [tableIsDirty, setTableIsDirty] = useState(false);
    const [impactSummaryData, setImpactSummaryData] = useState({});
    const { latitude, longitude } = requestFormDataLocation || {};

    const prevImpactsDataRef = useRef([]);
    const prevIlfDataRef = useRef([]);
    const prevPrmDataRef = useRef([]);

    const { data: waterNamesData } = useGetAquaticResourcesWaterNames();

    const projectWaters = useMemo(
      () =>
        waterNamesData?.data?.map((name) =>
          name ? { value: name, label: name } : { value: name, label: 'NO NAME ENTERED!' }
        ),
      [waterNamesData]
    );

    const impactsTableLength = impactsData?.length;
    const prmTableLength = prmData?.length;
    const ilfTableLength = ilfData?.length;
    const totalMitigationTableLength = prmTableLength + ilfTableLength;
    const districtName = requestFormData?.location?.district?.replace(/ District$/i, '');
    const columnHelper = createColumnHelper();
    const schemaArgs = {
      ilfTableErrors,
      ilfTableLength,
      impactsTableErrors,
      impactsTableLength,
      prmTableErrors,
      prmTableLength,
      totalMitigationTableLength,
    };

    const formSchema = isGeneralPermit ? getGpSchema(schemaArgs) : getIpSchema(schemaArgs);
    const methods = useForm({
      resolver: yupResolver(formSchema),
      mode: 'onBlur',
      defaultValues: getDefaultValues(permitData),
      shouldUnregister: true,
    });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      setError,
      clearErrors,
      trigger,
      reset,
      resetField,
    } = methods;

    const isCompensatoryMitigationProposed = watch('isCompensatoryMitigationProposed');
    const willResultInWetlandLoss = watch('willResultInWetlandLoss');

    const impactsValid = impactsData?.length > 0 && !impactsTableErrors;

    const prmValid =
      isCompensatoryMitigationProposed === 'true'
        ? (prmData?.length > 0 || ilfData?.length > 0) && !prmTableErrors
        : true;

    const ilfValid =
      isCompensatoryMitigationProposed === 'true'
        ? (ilfData?.length > 0 || prmData?.length > 0) && !ilfTableErrors
        : true;

    const isValid = Object.keys(errors).length === 0 && impactsValid && prmValid && ilfValid;
    const isDirty = Object.keys(dirtyFields).length > 0 || tableIsDirty;
    const isTouched = isSectionTouched(stepNo);

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSection]);

    useEffect(() => {
      const impactsTableDataChanged = !_isEqual(impactsData, prevImpactsDataRef.current);
      const ilfTableDataChanged = !_isEqual(ilfData, prevIlfDataRef.current);
      const prmTableDataChanged = !_isEqual(prmData, prevPrmDataRef.current);
      (impactsTableDataChanged || ilfTableDataChanged || prmTableDataChanged) && setTableIsDirty(true);
    }, [impactsData, ilfData, prmData]);

    // Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    // Calculations

    const proposedAcresCalc = useMemo(
      () =>
        impactsData
          ?.filter((item) => item?.amountUnit === 'Acres')
          .reduce((total, impact) => {
            if (impact.proposedAmount) {
              return total + Number(impact?.proposedAmount);
            }
            if (impact?.proposedLength && impact?.proposedWidth) {
              const area = impact?.proposedLength * impact?.proposedWidth;
              return total + Number(area / SQ_FT_PER_ACRE);
            }
            return total;
          }, 0),
      [impactsData]
    );

    const proposedSquareFtCalc = useMemo(
      () =>
        impactsData
          ?.filter((item) => item?.amountUnit === 'Square Feet')
          .reduce((total, impact) => {
            if (impact?.proposedAmount) {
              return total + Number(impact?.proposedAmount);
            }
            if (impact?.proposedLength && impact?.proposedWidth) {
              const area = impact?.proposedLength * impact?.proposedWidth;
              return total + Number(area);
            }
            return total;
          }, 0),
      [impactsData]
    );

    const proposedCubicYards = useMemo(
      () =>
        impactsData?.reduce((total, impact) => {
          if (impact?.amountUnit === 'Cubic Yards') {
            return total + Number(impact?.proposedAmount || 0); // Check if proposedAmount is null
          }
          return total;
        }, 0),
      [impactsData]
    );

    const isPermanentLoss = (item) => item?.isPermanentLoss === 'true' || item?.isPermanentLoss == true;

    const isWetlandResource = (item) =>
      item?.resourceType === ImpactsResourceType.NonTidalWetland ||
      item?.resourceType === ImpactsResourceType.TidalWetland;

    const isStreamResource = (item) => item?.resourceType === ImpactsResourceType.RiverStream;

    const totalWetlandLossCalc = useMemo(
      () =>
        (impactsData ?? [])
          .filter((item) => isPermanentLoss(item) && isWetlandResource(item))
          .reduce((total, impact) => total + toAcres(impact), 0) ?? 0,
      [impactsData]
    );

    const totalStreamLossCalc = useMemo(
      () =>
        (impactsData ?? [])
          .filter((item) => isPermanentLoss(item) && isStreamResource(item))
          .reduce((total, impact) => total + toAcres(impact), 0) ?? 0,
      [impactsData]
    );

    const impactsColumns = useMemo(
      () => [
        columnHelper.display({
          id: 'edit',
          cell: isReadOnly ? <></> : EditCell,
          size: 40,
          enableResizing: false,
          enableSorting: false,
        }),
        columnHelper.accessor('waterName', {
          header: 'Water Name/Label',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : WatersNameTableCell,
          size: 190,
          meta: {
            dataType: 'impacts',
            options: projectWaters,
            required: true,
            tooltip: tooltips.waterName,
          },
        }),
        columnHelper.accessor('impactName', {
          header: 'Impact Name',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 190,
          meta: {
            required: true,
            tooltip: tooltips.impactName,
            type: 'text',
          },
        }),
        columnHelper.accessor('activity', {
          header: 'Activity',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 190,
          meta: {
            options: options.impactActivities,
            required: true,
            tooltip: tooltips.activity,
            type: 'select',
          },
        }),
        columnHelper.accessor('dischargeMaterialType', {
          header: 'Type of Materials Being Discharged',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TypeOfMaterialsTableCell,
          size: 190,
          meta: {
            tooltip: tooltips.typeOfMaterials,
            type: 'text',
          },
        }),
        columnHelper.accessor('resourceType', {
          header: 'Resource Type',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 190,
          enableHiding: true,
          meta: {
            options: options.impactResourceTypes,
            required: true,
            tooltip: tooltips.resourceType,
            type: 'select',
          },
        }),
        columnHelper.accessor('isPermanentLoss', {
          header: 'Permanent Loss',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue() ? 'Yes' : 'No'}</span> : PermanentLossTableCell,
          size: 190,
          meta: {
            options: [
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ],
            required: true,
            tooltip: tooltips.permanentLoss,
            type: 'select',
          },
        }),
        columnHelper.accessor('impactDuration', {
          header: 'Impact Duration',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : ImpactTableCell,
          size: 190,
          meta: {
            options: [
              { value: 'Temporary', label: 'Temporary' },
              { value: 'Permanent', label: 'Permanent' },
            ],
            required: true,
            tooltip: tooltips.impactDuration,
            type: 'select',
          },
        }),
        columnHelper.accessor('amountType', {
          header: 'Amount Type',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : AmountTypeTableCell,
          size: 190,
          meta: {
            options: [
              { value: 'Fill Area', label: 'Fill Area' },
              { value: 'Fill Volume', label: 'Fill Volume' },
              { value: 'Removal Area', label: 'Removal Area' },
              { value: 'Removal Volume', label: 'Removal Volume' },
              { value: 'Structure Area', label: 'Structure Area' },
            ],
            required: true,
            tooltip: tooltips.amountType,
            type: 'select',
          },
        }),
        columnHelper.accessor('proposedLength', {
          header: 'Proposed Length (feet)',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : ImpactAreaAmountTableCell,
          size: 220,
          meta: {
            isNumber: true,
            maxLength: 17,
            required: true,
            tooltip: tooltips.proposedLength,
          },
        }),
        columnHelper.accessor('proposedWidth', {
          header: 'Proposed Width (feet)',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : ImpactAreaAmountTableCell,
          size: 175,
          meta: {
            isNumber: true,
            maxLength: 17,
            required: true,
            tooltip: tooltips.impactWidth,
          },
        }),
        columnHelper.accessor('proposedAmount', {
          header: 'Proposed Area Amount',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : ImpactAreaAmountTableCell,
          size: 175,
          meta: {
            isNumber: true,
            maxLength: 17,
            tooltip: tooltips.proposedArea,
            type: 'text',
          },
        }),
        columnHelper.accessor('amountUnit', {
          header: 'Area Units',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : AmountUnitTableCell,
          size: 190,
          meta: {
            options: [areaOptions, volumeOptions],
            required: true,
            tooltip: tooltips.areaUnits,
            type: 'select',
          },
        }),
      ],
      [columnHelper, isReadOnly, projectWaters]
    );

    const prmColumns = useMemo(
      () => [
        columnHelper.display({
          id: 'edit',
          cell: isReadOnly ? <></> : EditCell,
          size: 40,
          enableResizing: false,
          enableSorting: false,
        }),
        columnHelper.accessor('waterName', {
          header: 'Waters Name (i.e. Wetland A, Stream A)',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : WatersNameTableCell,
          size: 200,
          meta: {
            dataType: 'mitigations',
            options: projectWaters,
            required: true,
            tooltip: tooltips.waterName,
          },
        }),
        columnHelper.accessor('mitigationName', {
          header: 'Mitigation Name (i.e. All Wetlands, Stream A, etc.)',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 200,
          meta: {
            type: 'text',
            required: true,
          },
        }),
        columnHelper.accessor('mitigationType', {
          header: 'Mitigation Type',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 200,
          meta: {
            type: 'select',
            required: true,
            tooltip: tooltips.mitigationType,
            options: options.prmMitigationTypes,
          },
        }),
        columnHelper.accessor('permitteeResponsibleType', {
          header: 'Permitee Responsible Mitigation Type',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 200,
          meta: {
            type: 'select',
            tooltip: tooltips.permitteeResponsibleMitigationType,
            options: options.mitigationSubTypes,
          },
        }),
        columnHelper.accessor('resourceType', {
          header: 'Resource Type',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 175,
          meta: {
            options: options.impactResourceTypes,
            tooltip: tooltips.resourceType,
            type: 'select',
          },
        }),
        columnHelper.accessor('proposedLength', {
          header: 'Proposed Length (feet)',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : AreaAmountTableCell,
          meta: {
            isNumber: true,
            maxLength: 17,
            required: true,
            tooltip: tooltips.proposedLength,
          },
        }),
        columnHelper.accessor('proposedWidth', {
          header: 'Proposed Width (feet)',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : AreaAmountTableCell,
          meta: {
            isNumber: true,
            maxLength: 17,
            required: true,
            tooltip: tooltips.impactWidth,
          },
        }),
        columnHelper.accessor('proposedAmount', {
          header: 'Proposed Area Amount',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : AreaAmountTableCell,
          size: 175,
          meta: {
            isNumber: true,
            maxLength: 17,
            required: true,
            tooltip: tooltips.proposedArea,
          },
        }),
        columnHelper.accessor('amountUnit', {
          header: 'Area Units',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : MitigationAmountUnitTableCell,
          meta: {
            options: [areaOptions],
            required: true,
            tooltip: tooltips.areaUnits,
            type: 'select',
          },
        }),
      ],
      [columnHelper, isReadOnly, projectWaters]
    );

    const ilfColumns = useMemo(
      () => [
        columnHelper.display({
          id: 'edit',
          cell: isReadOnly ? <></> : EditCell,
          size: 40,
          enableResizing: false,
          enableSorting: false,
        }),
        columnHelper.accessor('mitigationName', {
          header: 'Mitigation Name (i.e. All Wetlands, Stream A, etc.)',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 320,
          meta: {
            type: 'text',
            required: true,
          },
        }),
        columnHelper.accessor('mitigationType', {
          header: 'Mitigation Type',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 190,
          meta: {
            type: 'select',
            required: true,
            tooltip: tooltips.mitigationType,
            options: options.ilfMitigationTypes,
          },
        }),
        columnHelper.accessor('nameOfBankOrILF', {
          header: 'Bank/ILF Name',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : BankILFTableCell,
          size: 300,
          meta: {
            type: 'select',
            required: true,
          },
        }),
        // NOTE: Hidden rendered column that gets set in nameOfBankOrILF column
        columnHelper.accessor('bankOrILFNumber', {
          header: 'Bank/ILF Number',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          enableHiding: true,
          meta: {
            type: 'text',
            readOnly: true,
            required: true,
          },
        }),
        columnHelper.accessor('proposedCreditAmount', {
          header: 'Proposed Credit Amount',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : ProposedCreditAmountTableCell,
          size: 190,
          meta: {
            type: 'text',
            isNumber: true,
            maxLength: 17,
          },
        }),
        columnHelper.accessor('creditUnit', {
          header: 'Credit Units',
          cell: isReadOnly
            ? ({ cell }) => <span>{cell.getValue()}</span>
            : (props) => <CreditUnitTableCell {...props} district={requestFormDataLocation?.district} />,
          size: 190,
          meta: {
            type: 'select',
            options: [options.mitigationBankTypes, options.inLieuFeeTypes],
          },
        }),
      ],
      [columnHelper, isReadOnly, requestFormDataLocation?.district]
    );

    const updateImpactsData = useCallback(
      (rowIndex, columnId, updatedValue) => {
        setImpactsData((oldData) => {
          const newData = oldData ? [...oldData] : null;
          if (newData && newData[rowIndex]) {
            // Update properties
            newData[rowIndex] = {
              ...newData[rowIndex],
              [columnId]: updatedValue,
            };
            return newData;
          }
        });
      },
      [setImpactsData]
    );

    const updateIlfData = useCallback(
      (rowIndex, columnId, updatedValue) => {
        setIlfData((oldData) => {
          const newData = oldData ? [...oldData] : null;
          if (newData && newData[rowIndex]) {
            // Update properties
            newData[rowIndex] = {
              ...newData[rowIndex],
              [columnId]: updatedValue,
            };
            return newData;
          }
        });
      },
      [setIlfData]
    );

    const updatePRMData = useCallback(
      (rowIndex, columnId, updatedValue) => {
        setPrmData((oldData) => {
          const newData = oldData ? [...oldData] : null;
          if (newData && newData[rowIndex]) {
            // Update properties
            newData[rowIndex] = {
              ...newData[rowIndex],
              [columnId]: updatedValue,
            };
            return newData;
          }
        });
      },
      [setPrmData]
    );

    const removeMultipleImpacts = useCallback(
      (indicesToRemove) => {
        setImpactsData((oldData) => {
          const newRows = oldData && oldData.filter((_, index) => !indicesToRemove.includes(index));
          return newRows;
        });
        setImpactsKey((old) => old + 1);
      },
      [setImpactsData, setImpactsKey]
    );

    const handleAddMultipleImpactRows = (rows) => {
      const impactsHeaderMap = {
        'water name/label': 'waterName',
        'impact name': 'impactName',
        activity: 'activity',
        'type of materials being discharged': 'dischargeMaterialType',
        'resource type': 'resourceType',
        'permanent loss': 'isPermanentLoss',
        'impact duration': 'impactDuration',
        'amount type': 'amountType',
        'proposed length (feet)': 'proposedLength',
        'proposed width (feet)': 'proposedWidth',
        'proposed area amount': 'proposedAmount',
        'area units': 'amountUnit',
      };

      const formatValue = (key, value) => {
        switch (key) {
          case 'state': {
            return formatState(value);
          }
          case 'permanent loss': {
            return formatBooleanString(value);
          }
          case 'activity': {
            return formatOptionString(value, options.impactActivities, false);
          }
          case 'impact duration':
          case 'amount type': {
            return formatProperCase(value);
          }
          case 'resource type': {
            return formatOptionString(value, options.impactResourceTypes, false);
          }
          case 'proposed length (feet)':
          case 'proposed width (feet)':
          case 'proposed area amount': {
            return formatNumericField(value);
          }
          case 'area units': {
            return formatMeasurementUnit(value);
          }
          default: {
            return value;
          }
        }
      };

      const mappedRows = rows.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [
            impactsHeaderMap[key.toLowerCase()] || key.toLowerCase(),
            formatValue(key.toLowerCase(), value),
          ])
        )
      );

      setImpactsData((oldData) => {
        const newRows = [...oldData, ...mappedRows];
        return newRows;
      });
    };

    const handleAddMultiplePrmRows = (rows) => {
      const prmHeaderMap = {
        'waters name (i.e. wetland a, stream a)': 'waterName',
        'mitigation name (i.e. all wetlands, stream a, etc.)': 'mitigationName',
        'mitigation type': 'mitigationType',
        'permitee responsible mitigation type': 'permitteeResponsibleType',
        'resource type': 'resourceType',
        'proposed length (feet)': 'proposedLength',
        'proposed width (feet)': 'proposedWidth',
        'proposed area amount': 'proposedAmount',
        'area units': 'amountUnit',
      };

      const formatValue = (key, value) => {
        switch (key) {
          case 'permitee responsible mitigation type': {
            return formatOptionString(value, options.mitigationSubTypes, false);
          }
          case 'mitigation type': {
            return formatOptionString(value, options.prmMitigationTypes, true);
          }
          case 'resource type': {
            return formatOptionString(value, options.impactResourceTypes, true);
          }
          case 'proposed length (feet)':
          case 'proposed width (feet)':
          case 'proposed area amount': {
            return formatNumericField(value);
          }
          case 'area units': {
            return formatMeasurementUnit(value);
          }
          default: {
            return value;
          }
        }
      };

      const mappedRows = rows.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [
            prmHeaderMap[key.toLowerCase()] || key.toLowerCase(),
            formatValue(key.toLowerCase(), value),
          ])
        )
      );

      setPrmData((oldData) => {
        const newRows = [...oldData, ...mappedRows];
        return newRows;
      });
    };

    const generateOptions = (data) => {
      const defaultBankILFOptions = [
        {
          label: 'Not Listed',
          value: 'Not Listed',
          id: 999999999,
        },
      ];
      const uniqueNames = new Set();
      const options = [...defaultBankILFOptions];

      data?.forEach((item) => {
        const name = item.name;
        if (!uniqueNames.has(name)) {
          uniqueNames.add(name);
          options.push({
            value: name,
            label: name,
            id: item.id,
          });
        }
      });
      return options;
    };

    const getBankIlfProgramOptions = async () => {
      if (useLegacyEndpoints) {
        // Get ILF and Bank Program Options using the Legacy API
        const ilfData = await RibitsQueries.getIlfProgramList('district', districtName);
        const bankData = await RibitsQueries.getBankSiteList('district', districtName);

        const bankProgramOptions = generateOptions([...bankData, ...ilfData]);

        return bankProgramOptions;
      } else {
        // Get ILF and Bank Program Options using the Test/Production API
        const ilfData = await RibitsQueries.getMitigationBanksOrIlf('In-Lieu Fee', latitude, longitude);
        const bankData = await RibitsQueries.getMitigationBanksOrIlf('Mitigation Bank', latitude, longitude);
        const bankProgramOptions = generateOptions([...bankData, ...ilfData]);

        return bankProgramOptions;
      }
    };

    const handleAddMultipleIlfRows = async (rows) => {
      const bankIlfOptions = await getBankIlfProgramOptions();

      const ilfHeaderMap = {
        'mitigation name (i.e. all wetlands, stream a, etc.)': 'mitigationName',
        'mitigation type': 'mitigationType',
        'bank/ilf name': 'nameOfBankOrILF',
        'proposed credit amount': 'proposedCreditAmount',
        'credit units': 'creditUnit',
      };

      const formatValue = (key, value) => {
        switch (key) {
          case 'mitigation type': {
            return formatOptionString(value, options.ilfMitigationTypes, false);
          }
          case 'bank/ilf name': {
            return formatOptionString(value, bankIlfOptions, false);
          }
          case 'proposed credit amount': {
            return String(value);
          }
          case 'credit units': {
            const unitTypes = [...options.mitigationBankTypes, ...options.inLieuFeeTypes];
            return formatOptionString(value, unitTypes, false);
          }
          default: {
            return value;
          }
        }
      };

      const mappedRows = rows.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [
            ilfHeaderMap[key.toLowerCase()] || key.toLowerCase(),
            formatValue(key.toLowerCase(), value),
          ])
        )
      );

      const mappedRowsWithIds = mappedRows.map((row) => {
        if ((!row.bankOrILFNumber || row.bankOrILFNumber === '') && row.nameOfBankOrILF && bankIlfOptions) {
          const matchedOption = bankIlfOptions.find((option) => option.label === row.nameOfBankOrILF);
          return {
            ...row,
            bankOrILFNumber: matchedOption ? String(matchedOption.id) : '999999999',
          };
        }
        return row;
      });

      setIlfData((oldData) => {
        const newRows = [...oldData, ...mappedRowsWithIds];
        return newRows;
      });
    };

    const removeMultiplePrm = useCallback(
      (indicesToRemove) => {
        setPrmData((oldData) => {
          const newRows = oldData.filter((_, index) => !indicesToRemove.includes(index));
          return newRows;
        });
        setPrmKey((old) => old + 1);
      },
      [setPrmData, setPrmKey]
    );

    const removeMultipleIlf = useCallback(
      (indicesToRemove) => {
        setIlfData((oldData) => {
          const newRows = oldData.filter((_, index) => !indicesToRemove.includes(index));
          return newRows;
        });
        setIlfKey((old) => old + 1);
      },
      [setIlfData, setIlfKey]
    );

    const handleChange = (event) => {
      const { name, value } = event?.target;
      switch (name) {
        case 'impactAdditionalDetails':
          setImpactSummaryData(value ? { ...impactSummaryData, ...{ [name]: value } } : {});
          break;
        default: {
          doUpdatePermitRequest({ [name]: value });
          break;
        }
      }
    };

    const autoWetlandLoss = isGeneralPermit && (totalWetlandLossCalc > 0.1 || totalStreamLossCalc > 0.03);

    useEffect(() => {
      if (autoWetlandLoss) {
        setValue('willResultInWetlandLoss', 'true', { shouldValidate: true });
        doUpdatePermitRequest({ willResultInWetlandLoss: 'true' });
      }
    }, [trigger, autoWetlandLoss, setValue, doUpdatePermitRequest]);

    useEffect(() => {
      setValue('proposedAcres', proposedAcresCalc, { shouldValidate: true });
    }, [proposedAcresCalc, setValue]);

    useEffect(() => {
      setValue('proposedLinearFeet', proposedSquareFtCalc, { shouldValidate: true });
    }, [proposedSquareFtCalc, setValue]);

    useEffect(() => {
      setValue('proposedCubicYards', proposedCubicYards, { shouldValidate: true });
    }, [proposedCubicYards, setValue]);

    // Update permits request object
    useEffect(() => {
      if (impactsData) {
        const impacts = impactsData?.map((item) => ({
          ...item,
          proposedLength: setNumberValue(item?.proposedLength, false),
          proposedWidth: setNumberValue(item?.proposedWidth, false),
          proposedAmount: setNumberValue(item?.proposedAmount, false),
        }));
        doUpdatePermitRequest({ impacts: impacts });
      }
      doUpdatePermitRequest({
        impactSummary: filterNullEmptyObjects(impactSummaryData) !== null ? impactSummaryData : null,
      });
      // Update calculated values
      if (
        impactsData?.length > 0 &&
        (proposedAcresCalc !== 0 || proposedSquareFtCalc !== 0 || proposedCubicYards !== 0)
      ) {
        doUpdatePermitRequestSub('impactSummary', { proposedAcres: proposedAcresCalc });
        doUpdatePermitRequestSub('impactSummary', { proposedLinearFeet: proposedSquareFtCalc });
        doUpdatePermitRequestSub('impactSummary', { proposedCubicYards: proposedCubicYards });
      } else {
        doUpdatePermitRequestSub('impactSummary', { proposedAcres: null });
        doUpdatePermitRequestSub('impactSummary', { proposedLinearFeet: null });
        doUpdatePermitRequestSub('impactSummary', { proposedCubicYards: null });
      }
    }, [
      impactsData,
      impactSummaryData,
      doUpdatePermitRequest,
      doUpdatePermitRequestSub,
      proposedAcresCalc,
      proposedCubicYards,
      proposedSquareFtCalc,
    ]);

    useEffect(() => {
      const ilfMitigation = ilfData?.map((item) => ({
        ...item,
        proposedCreditAmount: setNumberValue(item?.proposedCreditAmount, false),
        proposedLength: setNumberValue(item?.proposedLength, false),
        proposedWidth: setNumberValue(item?.proposedWidth, false),
        proposedAmount: setNumberValue(item?.proposedAmount, false),
      }));
      const prmMitigation = prmData?.map((item) => ({
        ...item,
        proposedCreditAmount: setNumberValue(item?.proposedCreditAmount, false),
        proposedLength: setNumberValue(item?.proposedLength, false),
        proposedWidth: setNumberValue(item?.proposedWidth, false),
        proposedAmount: setNumberValue(item?.proposedAmount, false),
      }));

      const mitigations = [...ilfMitigation, ...prmMitigation];

      doUpdatePermitRequest({ mitigations: mitigations });
    }, [ilfData, prmData, doUpdatePermitRequest]);

    useEffect(() => {
      if (requestAPIData?.request?.permits?.[0]?.impacts) {
        const impacts = requestAPIData?.request?.permits?.[0]?.impacts;
        setImpactsData(impacts);
        prevImpactsDataRef.current = impacts;
      }
      if (requestAPIData?.request?.permits?.[0]?.mitigations) {
        const ilf = requestAPIData?.request?.permits?.[0]?.mitigations.filter((data) =>
          ['Mitigation Bank', 'In-lieu Fee'].includes(data.mitigationType)
        );

        setIlfData(ilf);
        prevIlfDataRef.current = ilf;

        const prm = requestAPIData?.request?.permits?.[0]?.mitigations.filter((data) =>
          [
            'Permittee Responsible On-site',
            'Permittee Responsible Off-site',
            'Advanced Permittee Responsible Mitigation',
          ].includes(data.mitigationType)
        );
        setPrmData(prm);
        prevPrmDataRef.current = prm;
      }
      if (requestAPIData?.request?.permits?.[0]?.impactSummary) {
        setImpactSummaryData(requestAPIData?.request?.permits?.[0]?.impactSummary);
      }
    }, [requestAPIData]);

    useEffect(() => {
      const impactsValid = impactsData?.length > 0 && !impactsTableErrors;
      const prmValid =
        isCompensatoryMitigationProposed === 'true'
          ? (prmData?.length > 0 || ilfData?.length > 0) && !prmTableErrors
          : true;
      const ilfValid =
        isCompensatoryMitigationProposed === 'true'
          ? (ilfData?.length > 0 || prmData?.length > 0) && !ilfTableErrors
          : true;
      const sectionValid = isValid && impactsValid && prmValid && ilfValid;
      updateSection(stepNo, { valid: sectionValid ? true : false });
    }, [
      updateSection,
      errors,
      ilfData?.length,
      ilfTableErrors,
      impactsData?.length,
      impactsTableErrors,
      isCompensatoryMitigationProposed,
      isReadOnly,
      isValid,
      prmData?.length,
      prmTableErrors,
      stepNo,
    ]);

    useEffect(() => {
      if (isTouched) {
        if (impactsData?.length < 1) {
          setError('impactsTable', { type: 'custom', message: 'Impacts: At least one impact is required' });
        } else if (impactsData?.length > 0 && impactsTableErrors === true) {
          setError('impactsTable', { type: 'custom', message: 'Impacts: Table contains invalid rows' });
        } else if (!impactsTableErrors && impactsData?.length > 0) {
          clearErrors('impactsTable');
          trigger();
        } else {
          clearErrors('impactsTable');
          trigger();
        }
      }
    }, [impactsTableErrors, impactsData, isTouched, setError, clearErrors, trigger]);

    useEffect(() => {
      if (isTouched) {
        if (isCompensatoryMitigationProposed === 'true') {
          if (totalMitigationTableLength < 1) {
            setError('mitigationTable', { type: 'custom', message: 'Mitigation: At least one mitigation is required' });
          } else {
            if (prmTableErrors === true || ilfTableErrors === true) {
              setError('mitigationTable', { type: 'custom', message: 'Mitigation: Table contains invalid rows' });
            } else {
              clearErrors('mitigationTable');
              trigger();
            }
          }
        } else {
          clearErrors('mitigationTable');
          trigger();
        }
      }
    }, [
      isCompensatoryMitigationProposed,
      prmTableErrors,
      ilfTableErrors,
      totalMitigationTableLength,
      isTouched,
      clearErrors,
      setError,
      trigger,
    ]);

    useEffect(() => {
      if (!isGeneralPermit) {
        resetField('willResultInWetlandLoss');
        resetField('compensatoryMitigationExplanation');
        doUpdatePermitRequest({ willResultInWetlandLoss: null, compensatoryMitigationExplanation: null });
      }
    }, [isGeneralPermit, clearErrors, doUpdatePermitRequest, resetField]);

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader
          text='Project Impacts and Mitigation'
          subtext={isGeneralPermit ? textContent.gpFormHeaderSubtext : textContent.ipFormHeaderSubtext}
        >
          <p id='impactsTable'>{textContent.instructions}</p>
          <div className='margin-top-1'>
            Download PDF Bulk Upload Guide:
            <ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.ImpactMitBulkUploadGuide, FileTypes.Resource)}
                  title='Impacts and Mitigation Bulk Upload Guide [PDF, 2 pages]'
                  content='Impacts and Mitigation Bulk Upload Guide [PDF, 2 pages]'
                />
              </li>
            </ul>
          </div>
          <div className='margin-top-1 margin-bottom-2'>
            Download CSV Bulk Upload Template:
            <ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.ImpactBulkUploadCSV, FileTypes.Resource)}
                  title='Impact Bulk Upload CSV Template [CSV, 2 KB]'
                  content='Impact Bulk Upload CSV Template [CSV, 2 KB]'
                />
              </li>
              <ul>
                <li>Use for uploading impacts for related aquatic resources</li>
              </ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.PRMBulkUploadCSV, FileTypes.Resource)}
                  title='Permittee Responsible Mitigation Bulk Upload CSV Template [CSV, 2KB]'
                  content='Permittee Responsible Mitigation Bulk Upload CSV Template [CSV, 2KB]'
                />
              </li>
              <ul>
                <li>Use for uploading Permittee Responsible Mitigation for related aquatic resources</li>
              </ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.MitBankIlfBulkUploadCSV, FileTypes.Resource)}
                  title='Mitigation Bank Inlieu Fee Bulk Upload CSV Template [CSV, 2KB]'
                  content='Mitigation Bank Inlieu Fee Bulk Upload CSV Template [CSV, 2KB]'
                />
              </li>
              <ul>
                <li>Use for uploading Mitigation Bank or In-lieu Fee credit information</li>
              </ul>
            </ul>
          </div>
          <div>{textContent.buttonInstructions}</div>
          <p>{textContent.areaInstructions}</p>
          <p>{textContent.informativeVideo}</p>
        </StepHeader>

        <Section isRequired label='Impacts'>
          <TanStackTableNew
            addRow={() => setImpactsData((prev) => (prev ? [...prev, {}] : [{}]))}
            columns={impactsColumns}
            data={impactsData}
            hideDrag={false}
            initialTableState={{}}
            isInputTable={true}
            isReadOnly={isReadOnly}
            key={impactsKey}
            placeholderClick={() => setImpactsData((prev) => (prev ? [...prev, {}] : [{}]))}
            placeholderText='No Impacts found.'
            removeMultipleRows={removeMultipleImpacts}
            addMultipleRows={handleAddMultipleImpactRows}
            rowErrorCallback={setImpactsTableErrors}
            tableVersion='ImpactsTable'
            updateSourceData={updateImpactsData}
            validationSchema={impactsValidationSchema}
          />
          <div>
            <TextAreaInput
              label={
                isGeneralPermit
                  ? textContent.gpImpactAdditionalDetailsLabel
                  : textContent.ipImpactAdditionalDetailsLabel
              }
              name='impactAdditionalDetails'
              required
              readOnly={isReadOnly}
              onChange={handleChange}
            />
          </div>
        </Section>
        <Section label='Mitigation'>
          <div>
            <TextAreaInput
              label='Description of Avoidance, Minimization, and Compensation'
              name='proposedMitigationMeasures'
              required
              tooltip={tooltips.proposedMitigation}
              readOnly={isReadOnly}
              onChange={handleChange}
            />
          </div>
          <Alert className='margin-top-2' type='info' headingLevel='h3'>
            {textContent.locationInstructions}
          </Alert>
          {isGeneralPermit && (
            <div>
              <SelectInput
                className='width-50'
                fullWidth={false}
                label={textContent.willResultInWetlandLossLabel}
                name='willResultInWetlandLoss'
                onChange={handleChange}
                defaultValue={autoWetlandLoss}
                readOnly={isReadOnly || autoWetlandLoss}
                required
                tooltip={tooltips.willResultInWetlandLossLabel}
              >
                <option key='2' value='true'>
                  Yes
                </option>
                <option key='3' value='false'>
                  No
                </option>
              </SelectInput>
            </div>
          )}
          {isGeneralPermit && willResultInWetlandLoss === 'true' && (
            <div>
              <TextAreaInput
                label={textContent.compensatoryMitigationExplanation}
                name='compensatoryMitigationExplanation'
                required
                tooltip={tooltips.compensatoryMitigation}
                readOnly={isReadOnly}
                onChange={handleChange}
              />
            </div>
          )}
          <div>
            <SelectInput
              className='width-50'
              fullWidth={false}
              label='Do you propose compensatory mitigation?'
              name='isCompensatoryMitigationProposed'
              required
              readOnly={isReadOnly}
              onChange={handleChange}
            >
              <option key='2' value='true'>
                Yes
              </option>
              <option key='3' value='false'>
                No
              </option>
            </SelectInput>
          </div>
          {isCompensatoryMitigationProposed === 'true' && (
            <>
              <Section isSubsection label='Permittee Responsible Mitigation'>
                <TanStackTableNew
                  addMultipleRows={handleAddMultiplePrmRows}
                  addRow={() => setPrmData((prev) => (prev ? [...prev, {}] : [{}]))}
                  columns={prmColumns}
                  data={prmData}
                  hideDrag={false}
                  initialTableState={{}}
                  isInputTable={true}
                  isReadOnly={isReadOnly}
                  key={prmKey}
                  placeholderClick={() => setPrmData((prev) => (prev ? [...prev, {}] : [{}]))}
                  placeholderText={'No Permittee Responsible Mitigation found.'}
                  removeMultipleRows={removeMultiplePrm}
                  rowErrorCallback={setPrmTableErrors}
                  tableVersion='PRMTable'
                  updateSourceData={updatePRMData}
                  validationSchema={prmValidationSchema}
                />
              </Section>
              <Section isSubsection label='Mitigation Banks / In-Lieu Fee Programs' id='mitigationTable'>
                <TanStackTableNew
                  addMultipleRows={handleAddMultipleIlfRows}
                  addRow={() => setIlfData((prev) => (prev ? [...prev, {}] : [{}]))}
                  columns={ilfColumns}
                  data={ilfData}
                  hideDrag={false}
                  ignoredHeaders={['Bank/ILF Number']}
                  initialTableState={{ columnVisibility: { bankOrILFNumber: false } }}
                  isInputTable={true}
                  isReadOnly={isReadOnly}
                  key={ilfKey}
                  placeholderClick={() => setIlfData((prev) => (prev ? [...prev, {}] : [{}]))}
                  placeholderText={'No Mitigation Banks or In Lieu Fee Programs found.'}
                  removeMultipleRows={removeMultipleIlf}
                  rowErrorCallback={setIlfTableErrors}
                  updateSourceData={updateIlfData}
                  validationSchema={ilfValidationSchema}
                />
              </Section>
            </>
          )}
        </Section>
      </FormProvider>
    );
  }
);

PermitFormImpactsMitigation.metadata = PermitFormImpactsMitigationMetadata;

export default PermitFormImpactsMitigation;
